import { ApiError, apiRequest } from "./apiClient";
import type {
  KbSectionGroupDetail,
  KbSectionInputField,
  KbSectionListRow,
  KbSectionVersionDetail,
  KbTagOption,
} from "../types/gameRuleKbSection";

type KbSectionListResponse = {
  readonly success?: boolean;
  readonly data: unknown[];
  readonly meta: { readonly total: number; readonly page: number; readonly limit: number };
};

type SuccessResponse<T> = {
  readonly success?: boolean;
  readonly data: T;
  readonly message?: string;
};

async function parseDetail(response: Response): Promise<string> {
  const raw = await response.text();
  try {
    const parsed = JSON.parse(raw) as { detail?: string; message?: string };
    return parsed.detail ?? parsed.message ?? (raw || `HTTP ${response.status}`);
  } catch {
    return raw || `HTTP ${response.status}`;
  }
}

function parseInputField(raw: Record<string, unknown>): KbSectionInputField | null {
  if (typeof raw.id !== "string" || typeof raw.name !== "string") {
    return null;
  }
  return {
    id: raw.id,
    name: raw.name,
    fieldType: typeof raw.field_type === "string" ? raw.field_type : "Text",
    required: raw.required === true,
    hint: typeof raw.hint === "string" ? raw.hint : null,
    dropdownOptions: Array.isArray(raw.dropdown_options)
      ? raw.dropdown_options.filter((v): v is string => typeof v === "string")
      : null,
    docAnchor: typeof raw.doc_anchor === "string" ? raw.doc_anchor : null,
    docPlaceholder: typeof raw.doc_placeholder === "string" ? raw.doc_placeholder : null,
    isTagged: raw.is_tagged === true,
    sortOrder: typeof raw.sort_order === "number" ? raw.sort_order : 0,
  };
}

function parseVersionDetail(raw: Record<string, unknown>): KbSectionVersionDetail | null {
  if (typeof raw.id !== "string" || typeof raw.name !== "string") {
    return null;
  }
  const fieldsRaw = Array.isArray(raw.fields) ? raw.fields : [];
  const fields: KbSectionInputField[] = [];
  for (const f of fieldsRaw) {
    if (f && typeof f === "object") {
      const parsed = parseInputField(f as Record<string, unknown>);
      if (parsed) fields.push(parsed);
    }
  }
  return {
    id: raw.id,
    name: raw.name,
    description: typeof raw.description === "string" ? raw.description : null,
    contentPlain: typeof raw.content_plain === "string" ? raw.content_plain : "",
    extractedContent: typeof raw.extracted_content === "string" ? raw.extracted_content : null,
    tags: Array.isArray(raw.tags)
      ? raw.tags.filter((t): t is string => typeof t === "string")
      : [],
    fields,
    status: typeof raw.status === "string" ? raw.status : "",
    version: typeof raw.version === "number" ? raw.version : 1,
    sectionGroupId: typeof raw.section_group_id === "string" ? raw.section_group_id : null,
    isActive: raw.is_active === true,
    createdAt: typeof raw.created_at === "string" ? raw.created_at : null,
  };
}

function parseListRow(raw: Record<string, unknown>): KbSectionListRow | null {
  if (typeof raw.id !== "string" || typeof raw.name !== "string") {
    return null;
  }
  const groupId =
    typeof raw.section_group_id === "string" ? raw.section_group_id : raw.id;
  return {
    id: raw.id,
    name: raw.name,
    description: typeof raw.description === "string" ? raw.description : null,
    primaryTag: typeof raw.primary_tag === "string" ? raw.primary_tag : null,
    tags: Array.isArray(raw.tags)
      ? raw.tags.filter((t): t is string => typeof t === "string")
      : [],
    fieldCount: typeof raw.field_count === "number" ? raw.field_count : 0,
    status: typeof raw.status === "string" ? raw.status : "",
    version: typeof raw.version === "number" ? raw.version : 1,
    sectionGroupId: groupId,
    totalVersions: typeof raw.total_versions === "number" ? raw.total_versions : 1,
  };
}

function parseGroupDetail(raw: Record<string, unknown>): KbSectionGroupDetail | null {
  if (typeof raw.section_group_id !== "string" || typeof raw.name !== "string") {
    return null;
  }
  const versionsRaw = Array.isArray(raw.versions) ? raw.versions : [];
  const versions: KbSectionVersionDetail[] = [];
  for (const v of versionsRaw) {
    if (v && typeof v === "object") {
      const parsed = parseVersionDetail(v as Record<string, unknown>);
      if (parsed) versions.push(parsed);
    }
  }
  return {
    sectionGroupId: raw.section_group_id,
    name: raw.name,
    primaryTag: typeof raw.primary_tag === "string" ? raw.primary_tag : null,
    tags: Array.isArray(raw.tags)
      ? raw.tags.filter((t): t is string => typeof t === "string")
      : [],
    totalVersions: typeof raw.total_versions === "number" ? raw.total_versions : versions.length,
    activeVersionId: typeof raw.active_version_id === "string" ? raw.active_version_id : null,
    activeVersion: typeof raw.active_version === "number" ? raw.active_version : null,
    versions,
  };
}

export type ListKbSectionsParams = {
  readonly page: number;
  readonly limit: number;
  readonly search?: string;
  readonly tag?: string;
  readonly status?: string;
};

export async function listKbSectionsPaginated(
  params: ListKbSectionsParams,
): Promise<{ readonly items: KbSectionListRow[]; readonly total: number }> {
  const sp = new URLSearchParams();
  sp.set("page", String(params.page));
  sp.set("limit", String(params.limit));
  sp.set("status", params.status?.trim() || "active");
  if (params.search?.trim()) {
    sp.set("search", params.search.trim());
  }
  if (params.tag?.trim()) {
    sp.set("tag", params.tag.trim());
  }
  const response = await apiRequest(`/game-rule/kb/sections?${sp.toString()}`, undefined);
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as KbSectionListResponse;
  const rawList = Array.isArray(json.data) ? json.data : [];
  const items: KbSectionListRow[] = [];
  for (const row of rawList) {
    if (row && typeof row === "object") {
      const parsed = parseListRow(row as Record<string, unknown>);
      if (parsed) items.push(parsed);
    }
  }
  const total = typeof json.meta?.total === "number" ? json.meta.total : items.length;
  return { items, total };
}

export async function listKbTags(query?: string): Promise<readonly KbTagOption[]> {
  const path = query?.trim()
    ? `/game-rule/kb/tags?q=${encodeURIComponent(query.trim())}`
    : "/game-rule/kb/tags";
  const response = await apiRequest(path, undefined);
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<unknown[]>;
  const rows = Array.isArray(json.data) ? json.data : [];
  const tags: KbTagOption[] = [];
  for (const row of rows) {
    if (row && typeof row === "object") {
      const r = row as Record<string, unknown>;
      if (typeof r.id === "string" && typeof r.name === "string") {
        tags.push({ id: r.id, name: r.name });
      }
    }
  }
  return tags;
}

export async function getKbSectionGroupDetail(
  sectionGroupId: string,
): Promise<KbSectionGroupDetail> {
  const response = await apiRequest(
    `/game-rule/kb/sections/groups/${encodeURIComponent(sectionGroupId)}`,
    undefined,
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<Record<string, unknown>>;
  const parsed = parseGroupDetail(json.data ?? {});
  if (!parsed) {
    throw new ApiError("Invalid section group response", 502);
  }
  return parsed;
}

export async function activateKbSectionVersion(
  sectionGroupId: string,
  sectionId: string,
): Promise<void> {
  const response = await apiRequest(
    `/game-rule/kb/sections/groups/${encodeURIComponent(sectionGroupId)}/versions/${encodeURIComponent(sectionId)}/activate`,
    { method: "POST" },
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
}
